import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Check, Copy, Download, Info, KeyRound, Loader2, RefreshCw, Shield, X } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { ScrollArea } from "../ui/scroll-area";
import { getApiBase } from "../../lib/apiBase";
import { shuffleArray } from "../../lib/formationsDefaults";

/* ---------- Helpers ---------- */
const API_PREFIX = "/api/formations/tests/sessions";

const RETENTION_NOTICE_IN_PROGRESS = "Attention : votre test en cours est conservé pendant 30 jours. Pensez à garder votre code secret pour reprendre le test plus tard.";
const RETENTION_NOTICE_COMPLETED = "Votre test est terminé et verrouillé. Téléchargez votre correction en PDF pour la conserver. La correction restera accessible avec votre code secret pendant 7 jours seulement.";

const formatDateFr = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};
const formatDateTimeFr = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

/* Bandeau de rétention (in_progress 30 jours ou completed 7 jours). */
const RetentionBanner = ({ variant = "in_progress" }) => {
  const isDone = variant === "completed";
  return (
    <div
      className={`flex items-start gap-3 rounded-[1.4rem] border px-4 py-3 text-sm leading-6 sm:px-5 ${
        isDone
          ? "border-amber-200 bg-amber-50/70 text-amber-900"
          : "border-[#d8ece4] bg-[#f1fbf6] text-[#0f5b54]"
      }`}
      data-testid={`qcm-retention-banner-${variant}`}
    >
      {isDone ? <Shield className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#0f766e]" />}
      <p>{isDone ? RETENTION_NOTICE_COMPLETED : RETENTION_NOTICE_IN_PROGRESS}</p>
    </div>
  );
};

/* Génère et télécharge un PDF de correction. */
const downloadCorrectionPdf = (session, sessionQuestions) => {
  const score = session?.score || { total: 0, correct: 0, incorrect: 0, unanswered: 0, percentage: 0, appreciation: "" };
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // En-tête
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(15, 118, 110);
  doc.text("Bimcubes — Correction de test", marginX, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(session?.qcmTitle || "Test gratuit", marginX, 68);

  // Bloc méta
  const metaTop = 92;
  doc.setDrawColor(216, 236, 228);
  doc.setFillColor(248, 252, 243);
  doc.roundedRect(marginX, metaTop, pageWidth - marginX * 2, 92, 8, 8, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 118, 110);
  doc.text("CODE SECRET", marginX + 14, metaTop + 20);
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(13);
  doc.text(session?.code || "—", marginX + 14, metaTop + 38);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`Date de passage : ${formatDateTimeFr(session?.completedAt || session?.updatedAt)}`, marginX + 14, metaTop + 56);
  doc.text(`Nom : ${session?.name || "—"}`, marginX + 14, metaTop + 72);

  // Bloc score (à droite)
  const scoreBoxX = marginX + (pageWidth - marginX * 2) / 2 + 12;
  const scoreBoxW = (pageWidth - marginX * 2) / 2 - 12;
  doc.setDrawColor(217, 231, 213);
  doc.setFillColor(240, 253, 250);
  doc.roundedRect(scoreBoxX, metaTop, scoreBoxW, 92, 8, 8, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 118, 110);
  doc.text("SCORE", scoreBoxX + 14, metaTop + 20);
  doc.setFontSize(22);
  doc.setTextColor(15, 23, 42);
  doc.text(`${score.correct} / ${score.total}`, scoreBoxX + 14, metaTop + 46);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(`${score.percentage}% de réussite`, scoreBoxX + 14, metaTop + 64);
  doc.setFontSize(10);
  doc.setTextColor(34, 139, 34);
  doc.text(`✓ ${score.correct} correcte(s)`, scoreBoxX + 14, metaTop + 80);
  doc.setTextColor(190, 18, 60);
  doc.text(`✗ ${score.incorrect} incorrecte(s)`, scoreBoxX + 14 + 110, metaTop + 80);

  // Appréciation
  let cursorY = metaTop + 92 + 18;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  const wrapped = doc.splitTextToSize(score.appreciation || "", pageWidth - marginX * 2);
  doc.text(wrapped, marginX, cursorY);
  cursorY += wrapped.length * 12 + 10;

  // Section correction question par question
  sessionQuestions.forEach((q, index) => {
    const userId = (session?.responses || {})[q.id] || "";
    const isCorrect = userId === q.correctAnswerId;
    const userText = (q.answers.find((a) => a.id === userId) || {}).text || "(sans réponse)";
    const correctText = (q.answers.find((a) => a.id === q.correctAnswerId) || {}).text || "—";
    const status = isCorrect ? "Correct" : (userId ? "Incorrect" : "Sans réponse");
    const statusColor = isCorrect ? [16, 122, 87] : [190, 18, 60];

    autoTable(doc, {
      startY: cursorY,
      head: [[`Question ${index + 1}`, status]],
      body: [
        ["Énoncé", q.questionText || ""],
        ["Votre réponse", userText],
        ["Bonne réponse", correctText],
        ...(q.explanation ? [["Explication", q.explanation]] : []),
      ],
      styles: { font: "helvetica", fontSize: 9, cellPadding: 5, valign: "top", overflow: "linebreak" },
      headStyles: { fillColor: statusColor, textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 100, fontStyle: "bold", textColor: [80, 80, 80] }, 1: { cellWidth: pageWidth - marginX * 2 - 100 } },
      margin: { left: marginX, right: marginX },
    });
    cursorY = doc.lastAutoTable.finalY + 14;
  });

  // Pied de page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(`Bimcubes — Correction conservée 7 jours via le code ${session?.code || "—"} — Page ${i}/${pageCount}`, marginX, doc.internal.pageSize.getHeight() - 20);
  }

  const safeTitle = (session?.qcmTitle || "test").replace(/[^a-z0-9-]+/gi, "_").slice(0, 40);
  doc.save(`correction-${safeTitle}-${session?.code || "session"}.pdf`);
};

/* ---------- Pre-test : saisie du prénom + démarrage ---------- */
const PreTestScreen = ({ qcm, onStart, isCreating }) => {
  const [name, setName] = useState("");
  return (
    <div className="space-y-5" data-testid="qcm-pretest-screen">
      <RetentionBanner variant="in_progress" />
      <div className="rounded-[1.8rem] border border-[#d8ece4] bg-[#f8fcf3] p-6 shadow-[0_20px_50px_-42px_rgba(15,118,110,0.18)]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Avant de commencer</div>
        <h3 className="bim-display mt-2 text-xl font-semibold text-slate-950 sm:text-2xl">Comment vous appelez-vous ?</h3>
        <p className="mt-3 text-sm leading-7 text-slate-600">
          Saisissez votre prénom (facultatif), puis lancez le test. Un code secret vous sera fourni pour reprendre ou consulter votre test plus tard.
        </p>
        <div className="mt-5 space-y-2">
          <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor="qcm-pretest-name">Prénom</label>
          <Input
            autoFocus
            data-testid="qcm-pretest-name-input"
            id="qcm-pretest-name"
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex. Marie"
            value={name}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs leading-6 text-slate-500">Aucun email ni compte requis. Seul votre code secret permettra de reprendre.</p>
        <Button
          className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]"
          data-testid="qcm-pretest-start-button"
          disabled={isCreating}
          onClick={() => onStart(name.trim())}
          type="button"
        >
          {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Commencer le test
        </Button>
      </div>
    </div>
  );
};

/* ---------- Code secret : copie + démarrage des questions ---------- */
const CodeReveal = ({ code, onContinue }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copié dans le presse-papier.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier automatiquement. Copiez le code manuellement.");
    }
  };
  return (
    <div className="space-y-5" data-testid="qcm-code-reveal-screen">
      <div className="rounded-[1.8rem] border border-[#d8ece4] bg-[linear-gradient(180deg,_rgba(240,253,250,0.9)_0%,_rgba(255,255,255,0.98)_100%)] p-6 text-center shadow-[0_24px_60px_-46px_rgba(15,118,110,0.22)]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Votre code secret</div>
        <div className="bim-display mt-3 text-3xl font-bold tracking-[0.18em] text-slate-950 sm:text-4xl" data-testid="qcm-code-reveal-value">{code}</div>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-slate-600">
          Conservez précieusement ce code. Vous pourrez l'utiliser plus tard pour reprendre ou consulter votre test, depuis n'importe quel appareil.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button
            className="rounded-full border-[#d8ece4] bg-white text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]"
            data-testid="qcm-code-copy-button"
            onClick={handleCopy}
            type="button"
            variant="outline"
          >
            {copied ? <Check className="mr-2 h-4 w-4 text-emerald-600" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Code copié" : "Copier le code"}
          </Button>
          <Button
            className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]"
            data-testid="qcm-code-continue-button"
            onClick={onContinue}
            type="button"
          >
            Commencer les questions
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Questions (avec autosave) ---------- */
const InProgressScreen = ({ session, sessionQuestions, responses, onSelectAnswer, savingCount, onFinish, isFinishing }) => {
  const totalQuestions = sessionQuestions.length;
  const answeredCount = Object.keys(responses).length;
  return (
    <div className="space-y-6" data-testid="qcm-in-progress-screen">
      <RetentionBanner variant="in_progress" />
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-[1.6rem] border border-[#d8ece4] bg-[#f8fcf3] px-5 py-4">
        <div>
          <div className="text-sm text-slate-600">{totalQuestions} questions · {answeredCount} réponse(s) sélectionnée(s)</div>
          <div className="mt-1 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0f766e]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0f766e]" />
            Code : <span className="tracking-[0.18em]">{session.code}</span>
          </div>
        </div>
        <div className="text-xs text-slate-500" data-testid="qcm-autosave-indicator">
          {savingCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[#0f766e]"><Loader2 className="h-3 w-3 animate-spin" /> Sauvegarde…</span>
          ) : (
            <span className="inline-flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-600" /> Sauvegardé automatiquement</span>
          )}
        </div>
      </div>

      {sessionQuestions.map((question, index) => (
        <div className="rounded-[1.8rem] border border-[#d9e7d5] bg-white p-5 shadow-[0_20px_50px_-42px_rgba(15,118,110,0.16)] sm:p-6" data-testid={`qcm-test-question-card-${question.id}`} key={question.id}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Question {index + 1}</div>
          <h3 className="mt-3 text-lg font-semibold leading-8 text-slate-950">{question.questionText}</h3>
          {question.imageUrl ? (
            <div className="mt-4 overflow-hidden rounded-[1.4rem] border border-[#d8ece4] bg-[#f8fcf3]">
              <img alt={question.questionText} className="h-auto max-h-[18rem] w-full object-contain" src={question.imageUrl} />
            </div>
          ) : null}
          <RadioGroup
            className="mt-5 space-y-3"
            onValueChange={(value) => onSelectAnswer(question.id, value)}
            value={responses[question.id] || ""}
          >
            {question.answers.map((answer) => (
              <label
                className="flex cursor-pointer items-start gap-3 rounded-[1.2rem] border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm leading-7 text-slate-700 transition hover:border-[#99f6e4] hover:bg-white"
                data-testid={`qcm-test-answer-option-${question.id}-${answer.id}`}
                htmlFor={`${question.id}-${answer.id}`}
                key={answer.id}
              >
                <RadioGroupItem className="mt-1 border-[#0f766e] text-[#0f766e]" id={`${question.id}-${answer.id}`} value={answer.id} />
                <span>{answer.text}</span>
              </label>
            ))}
          </RadioGroup>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm leading-7 text-slate-500">Une seule bonne réponse est attendue par question.</p>
        <Button
          className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]"
          data-testid="qcm-test-finish-button"
          disabled={isFinishing}
          onClick={onFinish}
          type="button"
        >
          {isFinishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Terminer le test
        </Button>
      </div>
    </div>
  );
};

/* ---------- Résultat + correction détaillée ---------- */
const CompletedScreen = ({ session, sessionQuestions, onRestart, isRestarting }) => {
  const score = session.score || { total: 0, correct: 0, incorrect: 0, unanswered: 0, percentage: 0, appreciation: "" };
  const handleDownload = () => {
    try {
      downloadCorrectionPdf(session, sessionQuestions);
      toast.success("PDF de correction téléchargé.");
    } catch (err) {
      toast.error("Téléchargement PDF impossible.");
    }
  };
  return (
    <div className="space-y-6" data-testid="qcm-completed-screen">
      <RetentionBanner variant="completed" />
      <div className="rounded-[2rem] border border-[#d9e7d5] bg-[linear-gradient(180deg,_rgba(248,252,243,0.9)_0%,_rgba(255,255,255,0.98)_100%)] p-8 text-center shadow-[0_26px_70px_-50px_rgba(15,118,110,0.22)]">
        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Résultat</div>
        <div className="bim-display mt-4 text-4xl font-semibold text-slate-950 sm:text-5xl" data-testid="qcm-completed-score">
          {score.correct} / {score.total}
        </div>
        <div className="mt-3 text-lg font-medium text-slate-700" data-testid="qcm-completed-percentage">{score.percentage}% de réussite</div>
        <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 ring-1 ring-emerald-100">
            <Check className="h-3 w-3" /> {score.correct} correcte(s)
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700 ring-1 ring-rose-100">
            <X className="h-3 w-3" /> {score.incorrect} incorrecte(s)
          </span>
          {score.unanswered > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 font-medium text-slate-700 ring-1 ring-slate-200">
              {score.unanswered} sans réponse
            </span>
          ) : null}
        </div>
        <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base" data-testid="qcm-completed-appreciation">{score.appreciation}</p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
          <KeyRound className="h-3.5 w-3.5 text-[#0f766e]" />
          Code : <span className="tracking-[0.18em]">{session.code}</span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Correction détaillée</div>
        {sessionQuestions.map((question, index) => {
          const userAnswerId = (session.responses || {})[question.id] || "";
          const correctId = question.correctAnswerId;
          const isCorrect = userAnswerId === correctId;
          const isUnanswered = !userAnswerId;
          return (
            <div
              className={`rounded-[1.8rem] border p-5 shadow-[0_20px_50px_-42px_rgba(15,118,110,0.16)] sm:p-6 ${isCorrect ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/30"}`}
              data-testid={`qcm-correction-card-${question.id}`}
              key={question.id}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Question {index + 1}</div>
                {isCorrect ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                    <Check className="h-3 w-3" /> Correct
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                    <X className="h-3 w-3" /> {isUnanswered ? "Sans réponse" : "Incorrect"}
                  </span>
                )}
              </div>
              <h3 className="mt-3 text-lg font-semibold leading-8 text-slate-950">{question.questionText}</h3>
              <ul className="mt-4 space-y-2">
                {question.answers.map((a) => {
                  const isUser = a.id === userAnswerId;
                  const isCorrectAnswer = a.id === correctId;
                  let cls = "border-slate-200 bg-white text-slate-700";
                  if (isCorrectAnswer) cls = "border-emerald-300 bg-emerald-50 text-emerald-900";
                  else if (isUser && !isCorrectAnswer) cls = "border-rose-300 bg-rose-50 text-rose-900";
                  return (
                    <li className={`flex items-center justify-between gap-3 rounded-[1rem] border px-3 py-2 text-sm leading-7 ${cls}`} key={a.id}>
                      <span>{a.text}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        {isUser ? <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Votre réponse</span> : null}
                        {isCorrectAnswer ? <Check className="h-4 w-4 text-emerald-700" /> : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {question.explanation ? (
                <div className="mt-4 rounded-[1rem] border border-slate-200 bg-white px-4 py-3 text-sm leading-7 text-slate-600">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0f766e]">Explication</div>
                  <p className="mt-1">{question.explanation}</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-center gap-3 pt-2">
        <Button
          className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]"
          data-testid="qcm-completed-download-pdf-button"
          onClick={handleDownload}
          type="button"
        >
          <Download className="mr-2 h-4 w-4" />
          Télécharger ma correction en PDF
        </Button>
        <Button
          className="rounded-full border-[#d8ece4] bg-white text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]"
          data-testid="qcm-completed-restart-button"
          disabled={isRestarting}
          onClick={onRestart}
          type="button"
          variant="outline"
        >
          {isRestarting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refaire le test (nouveau code)
        </Button>
      </div>
    </div>
  );
};

/* ---------- Composant principal ---------- */
/**
 * QcmTestDialog
 * Props :
 *   - qcm : objet QCM (id, title, description, questions[]) ouvre le dialog si non null.
 *   - resumeCode : si fourni, ouvre directement le dialog en chargeant cette session.
 *   - onClose : callback de fermeture.
 */
export const QcmTestDialog = ({ qcm, resumeCode = null, onClose }) => {
  // phase: "pretest" | "code" | "in_progress" | "completed"
  const [phase, setPhase] = useState("pretest");
  const [session, setSession] = useState(null);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [savingCount, setSavingCount] = useState(0);
  const [loadingResume, setLoadingResume] = useState(false);

  const open = Boolean(qcm) || Boolean(resumeCode);
  const apiBase = useMemo(() => getApiBase(), []);

  const resetAll = useCallback(() => {
    setPhase("pretest");
    setSession(null);
    setSessionQuestions([]);
    setResponses({});
    setIsCreating(false);
    setIsFinishing(false);
    setSavingCount(0);
    setLoadingResume(false);
  }, []);

  // Mémorise l'ordre tiré côté client pour stabilité d'affichage,
  // mais la sauvegarde est côté serveur, donc l'ordre est cosmétique.
  const initSessionQuestions = useCallback((apiQuestions, shuffle = true) => {
    const list = (apiQuestions || []).map((q) => ({ ...q, answers: [...(q.answers || [])] }));
    setSessionQuestions(shuffle ? shuffleArray(list) : list);
  }, []);

  /* --- Reprise via code (route ?session=CODE ou prop resumeCode) --- */
  useEffect(() => {
    if (!resumeCode) return;
    let cancelled = false;
    setLoadingResume(true);
    axios
      .get(`${apiBase}${API_PREFIX}/${encodeURIComponent(resumeCode)}`)
      .then((res) => {
        if (cancelled) return;
        const s = res.data;
        setSession(s);
        setResponses(s.responses || {});
        // Pour la correction, on garde l'ordre du back ; pour le test en cours,
        // on garde aussi l'ordre du back (stable d'une visite à l'autre).
        initSessionQuestions(s.questions, false);
        setPhase(s.status === "completed" ? "completed" : "in_progress");
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Code introuvable ou expiré.";
        toast.error(detail);
        onClose?.();
      })
      .finally(() => { if (!cancelled) setLoadingResume(false); });
    return () => { cancelled = true; };
  }, [resumeCode, apiBase, initSessionQuestions, onClose]);

  /* --- Réinitialise quand le dialog se ferme --- */
  useEffect(() => {
    if (!open) resetAll();
  }, [open, resetAll]);

  /* --- Démarrage : crée une session --- */
  const handleStart = async (name) => {
    if (!qcm) return;
    setIsCreating(true);
    try {
      const res = await axios.post(`${apiBase}${API_PREFIX}`, { qcmId: qcm.id, name });
      const s = res.data;
      setSession(s);
      setResponses(s.responses || {});
      initSessionQuestions(s.questions, true);
      setPhase("code");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Création de session impossible.";
      toast.error(detail);
    } finally {
      setIsCreating(false);
    }
  };

  /* --- Autosave d'une réponse --- */
  const inflightRef = useRef(0);
  const handleSelectAnswer = async (questionId, answerId) => {
    if (!session || session.status === "completed") return;
    setResponses((prev) => ({ ...prev, [questionId]: answerId }));
    inflightRef.current += 1;
    setSavingCount((c) => c + 1);
    try {
      await axios.patch(`${apiBase}${API_PREFIX}/${encodeURIComponent(session.code)}/answer`, {
        questionId, answerId,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sauvegarde impossible.";
      toast.error(detail);
    } finally {
      inflightRef.current -= 1;
      setSavingCount((c) => Math.max(0, c - 1));
    }
  };

  /* --- Terminer le test --- */
  const handleFinish = async () => {
    if (!session) return;
    setIsFinishing(true);
    try {
      const res = await axios.post(`${apiBase}${API_PREFIX}/${encodeURIComponent(session.code)}/finish`);
      const s = res.data;
      setSession(s);
      setResponses(s.responses || {});
      initSessionQuestions(s.questions, false); // ordre serveur, stable
      setPhase("completed");
      toast.success("Test terminé. Bonne lecture de la correction !");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Finalisation impossible.";
      toast.error(detail);
    } finally {
      setIsFinishing(false);
    }
  };

  /* --- Refaire le test (nouvelle session, nouveau code) --- */
  const handleRestart = async () => {
    if (!session) return;
    setIsCreating(true);
    try {
      const res = await axios.post(`${apiBase}${API_PREFIX}`, { qcmId: session.qcmId, name: session.name || "" });
      const s = res.data;
      setSession(s);
      setResponses({});
      initSessionQuestions(s.questions, true);
      setPhase("code");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Nouveau test impossible.";
      toast.error(detail);
    } finally {
      setIsCreating(false);
    }
  };

  const title = session?.qcmTitle || qcm?.title || "Test gratuit";
  const description = session?.qcmDescription || qcm?.description || "";

  return (
    <Dialog onOpenChange={(o) => { if (!o) onClose?.(); }} open={open}>
      <DialogContent className="max-w-5xl overflow-hidden rounded-[2rem] border-[#d9e7d5] bg-white p-0 shadow-[0_34px_100px_-54px_rgba(15,118,110,0.35)]" data-testid="qcm-test-dialog-content">
        <DialogHeader className="border-b border-[#e8f2ee] bg-[linear-gradient(180deg,_rgba(248,252,243,0.95)_0%,_rgba(255,255,255,0.98)_100%)] px-6 py-6 text-left sm:px-8 sm:py-8">
          <DialogTitle className="bim-display text-2xl font-semibold tracking-tight text-slate-950" data-testid="qcm-test-dialog-title">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="mt-2 max-w-3xl text-sm leading-7 text-slate-600" data-testid="qcm-test-dialog-description">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <ScrollArea className="max-h-[74vh]">
          <div className="px-6 py-6 sm:px-8 sm:py-8">
            {loadingResume ? (
              <div className="flex items-center justify-center py-12 text-sm text-slate-500" data-testid="qcm-resume-loading">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement de votre session…
              </div>
            ) : phase === "pretest" ? (
              <PreTestScreen isCreating={isCreating} onStart={handleStart} qcm={qcm} />
            ) : phase === "code" ? (
              <CodeReveal code={session?.code || ""} onContinue={() => setPhase("in_progress")} />
            ) : phase === "in_progress" ? (
              <InProgressScreen
                isFinishing={isFinishing}
                onFinish={handleFinish}
                onSelectAnswer={handleSelectAnswer}
                responses={responses}
                savingCount={savingCount}
                session={session}
                sessionQuestions={sessionQuestions}
              />
            ) : phase === "completed" ? (
              <CompletedScreen
                isRestarting={isCreating}
                onRestart={handleRestart}
                session={session}
                sessionQuestions={sessionQuestions}
              />
            ) : null}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
